import { createStore } from 'react-sweet-state';
import { cloneDeep, isEqual, isInteger, omit, sortBy, trim } from 'lodash';

import {
  htmlToSlate,
  replaceFilePlaceholders,
  replacePromptTags,
  slateToText,
} from 'src/components/RichSlate/lib';
import {
  customerCanChangeGenerationLanguage,
  customerCanChangeNumberOfVariations,
} from 'src/data/accessRights';
import { isNotTalkingPointAnnotation } from 'src/data/annotation';
import {
  BRIEF_TYPE,
  checkBriefHasContent,
  EMPTY_FIELD_ERROR,
  generateBriefCacheKey,
  getRecomposeTextFromBrief,
  isEnoughContentForExtraction,
  MANUAL_INSTRUCTIONS_MAX_CHARS,
  mapBriefFromGenerationResult,
  mapExtractedContentForEditing,
  validateDocumentBrief,
  validateFreestyleBrief,
} from 'src/data/brief';
import { MAX_RESOURCE_PER_TYPE } from 'src/data/common';
import {
  AI_RECOMPOSE_MODE,
  AI_SUGGESTION_MODE,
  alignLanguage,
  DEFAULT_NUMBER_OF_VARIATIONS,
  EMPTY_ALL_INSTRUCTIONS_DATA,
  GENERATION_GUIDANCE_TYPE,
  GENERATION_MODE_TYPE,
  getAlignedSuggestionMode,
  getDocumentDefaultNumberOfVariations,
  getLanguageTargetFormality,
  isNonEnglish,
  LANGUAGE,
  normalizeLanguage,
} from 'src/data/generation';
import {
  actualizeGenerationTool,
  alignToolForGeneration,
  doesToolHasMultipleGenerationModes,
  doesToolSupportBackgroundUrlBriefExtraction,
  doesToolSupportCampaignKeywords,
  doesToolSupportEnhancedImprovements,
  doesToolSupportHashtags,
  doesToolSupportManualInstructions,
  doesToolSupportMaxChars,
  doesToolSupportPromptInstruction,
  doesToolSupportsRewriteProductName,
  GENERATION_TOOL,
  getDefaultCaseType,
  getDefaultCharsByTool,
  getToolBriefType,
  getToolDefaultAssetType,
  getToolDefaultGenerationMode,
  getToolGenerationModes,
  getToolMaxCampaignKeywords,
  getToolMaxHashtags,
  getToolMaxSuggestKeywords,
  getToolMaxWordsPerKeyword,
  getToolMixtureSubGenerationTools,
  getToolMixtureSubGenerationToolsModeOverride,
  isBasicGenerationTool,
  isFreestyleGenerationTool,
  isLandingSeoMetaDataGenerationTool,
  isMixtureTool,
  isMultiPromptTool,
  isRewriteGenerationTool,
  isUsedTemplate,
} from 'src/data/generationTool';
import { DEFAULT_IMAGE_ASPECT_RATIO, DEFAULT_IMAGE_MODEL } from 'src/data/image';
import {
  getDefaultSuggestedAnnotationsOrigin,
  isImageResource,
  isNonImageFileResource,
  SUGGESTED_ANNOTATIONS_ORIGIN,
  USER_ANNOTATIONS_ORIGIN,
} from 'src/data/performance';
import { isBareboneProject } from 'src/data/project';
import { SUGGEST_PROMPT_TYPES } from 'src/data/suggestPrompt';
import {
  doesToolSupportsPersonaForMode,
  doesToolSupportSuggestKeywordsForMode,
} from 'src/data/utils';
import { MAX_CHARS_MAX_VALUE, validateUrl } from 'src/data/validation';
import { MAX_CHARS_ERRORS } from 'src/data/validation';
import { extractBrief, updateBrandBriefUsed } from 'src/graphql/brief';
import { uploadCsvFile } from 'src/graphql/customerMultiPromptTemplates';
import { updateProjectPersonas } from 'src/graphql/customerPersona';
import { suggestPrompt } from 'src/graphql/generation/suggestPrompt';
import { generationToolRatings } from 'src/graphql/generationTool';
import { getQueryParamValue } from 'src/lib/browser';
import { parseErrors } from 'src/lib/graphql';
import * as ls from 'src/lib/localData';
import { captureMessage } from 'src/lib/sentry';
import { countWords } from 'src/lib/string';
import { ROUTE_PARAMS } from 'src/routes';
import { waitForBriefExtraction } from 'src/services';
import { extractUrls } from 'src/utils/extractUrl';
import { areAllUrlsExtracted, getSuccessfulExtractions } from 'src/utils/extractUrl';
import { pluralize } from 'src/utils/format';

import { MANUAL_SUGGEST_IMPROVEMENT } from '../../../const';
import { INFINITY_EXTENSION_MODE } from '../const';
import { serializeMultiPromptFields, serializeToolPrompts } from '../utils';
import { getStepIndex, MULTI_PROMPT_TEMPLATE_STEPS } from '../views/MultiPromptTemplatePopup/const';

import { filterVisibleDDTemplates, getProjectLatestGenerationResult } from './utils';

const MAX_WORDS_PER_CALL_TO_ACTION = 10;
const LANGUAGE_SOURCE = {
  other: 'other',
  user: 'user',
};

const EMPTY_FREESTYLE_CONTENT = {
  id: null,
  name: '',
  allInstructions: '',
  allInstructionsData: null,
  personalizedPrompt: false,
  improvedPrompt: false,
};

const INITIAL_STATE = {
  generationTool: null,
  customMultiPromptId: null,
  subGenerationToolsProps: {},

  briefContent: null,
  briefContentCache: {},
  freestyleToolContent: EMPTY_FREESTYLE_CONTENT,
  isSuggestPromptLoading: false,

  rewriteText: '',
  rewriteProductName: '',
  rewriteProductNameCache: '',
  rewriteLanguage: LANGUAGE.DEFAULT,

  availableDDTemplates: [],
  visibleDDTemplates: [],
  selectedModeId: null,
  selectedModeType: null,

  selectedCustomerPersona: null,
  selectedCustomerPersonaCache: null,
  isUpdatingProjectPersonas: false,

  newAnnotationText: '',
  newCampaignKeywordsText: '',
  newHashtagText: '',
  annotations: [],

  isUpdatingProductAttributes: false,
  isFreestyleToolSaveViewVisible: false,
  isFreestyleToolConfirmChangesViewVisible: false,

  numberOfVariations: DEFAULT_NUMBER_OF_VARIATIONS,
  language: LANGUAGE.DEFAULT,
  languageSource: LANGUAGE_SOURCE.other,
  isOnlyImproveScore: false,

  errors: null,
  briefError: {},
  keywordsError: {},
  generating: false,
  disabledGeneration: false,

  selectedRecomposeTovId: null,
  selectedRecomposeTovType: null,

  manualInstructions: '',
  maxChars: null,
  casing: null,
  rewriteInstructions: undefined,
  selectedInstructionsOption: AI_RECOMPOSE_MODE.relaxed,

  extractedBriefs: {}, // url => content
  extractedBriefIds: {}, // url => id
  currentUrls: [],
  extractedBriefErrors: {},

  errorBriefUrl: null,
  errorBriefContent: null,
  errorBriefGeneral: null,
  errorBriefGeneralCode: null,
  extractedBrief: {},
  isExtractingBrief: false,
  shouldWaitForBrief: false,
  briefExtractionsInProgress: {},
  showImportFromUrl: false,
  popoverContent: { body: '' },

  showAdvancedOptions: false,
  areAdvancedOptionsModified: {},
  generationToolOptionsCache: {},

  suggestedAnnotationsOrigin: SUGGESTED_ANNOTATIONS_ORIGIN.anyword,

  isCustomFormulasVisible: false,
  callToAction: '',
  selectedContentImproverTool: GENERATION_TOOL.basicContentImprover,

  infinityExtensionMode: null, // the "mode" in which `CanvasProject` is used in the "Infinity" extension context

  // multi prompt fields
  multiPromptFields: null,
  toolPrompts: null,
  multiPromptActiveStep: MULTI_PROMPT_TEMPLATE_STEPS[0],

  isManagePersonasPopupOpen: false,

  messagingBank: [],

  promptImageErrors: {},
  isUploadingImage: false,
  uploadingImages: [],

  promptDocumentErrors: {},
  isUploadingDocument: false,
  multiPromptFieldDocuments: {},
  multiPromptFieldImages: {},
  uploadingDocuments: [],

  uploadedCsv: null,
  isCsvLoading: false,

  preSuggestedPromptContent: '',

  imageModel: DEFAULT_IMAGE_MODEL,
  imageAspectRatio: DEFAULT_IMAGE_ASPECT_RATIO,
};

const setError =
  (field, error) =>
  ({ getState, setState }) => {
    const { errors } = getState();
    setState({ errors: { ...errors, [field]: error } });
  };

const setSubGenerationToolError =
  (subGenerationTool, field, error) =>
  ({ getState, setState }) => {
    const { subGenerationToolsProps } = getState();
    if (!subGenerationToolsProps[subGenerationTool]) {
      return;
    }
    subGenerationToolsProps[subGenerationTool].errors = {
      ...subGenerationToolsProps[subGenerationTool].errors,
      [field]: error,
    };
    const newSubGenerationToolsProps = { ...subGenerationToolsProps };
    setState({ subGenerationToolsProps: newSubGenerationToolsProps });
  };

const isRecomposeMode = (mode) => Object.values(AI_RECOMPOSE_MODE).includes(mode);

const validateCallToActionText = (value) => {
  if (countWords(value) > MAX_WORDS_PER_CALL_TO_ACTION) {
    return {
      valid: false,
      message: `Call To Action can be maximum ${MAX_WORDS_PER_CALL_TO_ACTION} words.`,
    };
  }

  return { valid: true };
};

const toolCacheKey = (generationTool, customMultiPromptId) =>
  generationTool + (customMultiPromptId || '');

/**
 * Private actions
 */
export const PRIVATE_ACTIONS = {
  init:
    () =>
    ({ setState, dispatch }, { project }) => {
      const firstPersona =
        project?.customerPersonas?.length > 0 ? project.customerPersonas[0] : null;

      setState({
        ...cloneDeep(INITIAL_STATE),
        selectedCustomerPersona: firstPersona,
        annotations: project?.annotations || [],
      });

      dispatch(PRIVATE_ACTIONS.initGenerationLanguage());
      dispatch(PRIVATE_ACTIONS.initFromGenerationResult());
      dispatch(ACTIONS.initSubGenerationToolsProps());
      dispatch(PRIVATE_ACTIONS.initFreestyleToolFromGenerationResult());
      dispatch(ACTIONS.shouldExtractUrlBrief());

      const generationToolFromUrl = getQueryParamValue(ROUTE_PARAMS.generationTool);
      if (isRewriteGenerationTool(generationToolFromUrl)) {
        setState({ selectedContentImproverTool: generationToolFromUrl });
      }
      const customMultiPromptIdToolFromUrl = getQueryParamValue(ROUTE_PARAMS.customMultiPromptId);
      if (customMultiPromptIdToolFromUrl) {
        setState({ customMultiPromptId: customMultiPromptIdToolFromUrl });
      }
    },

  update: () => () => {},

  getCampaignKeywordsAnnotations:
    () =>
    ({ getState }) =>
      getState().annotations.filter(
        (annotation) => annotation.type === GENERATION_GUIDANCE_TYPE.campaignKeyword
      ),

  getHashtagAnnotations:
    () =>
    ({ getState }) =>
      getState().annotations.filter(
        (annotation) => annotation.type === GENERATION_GUIDANCE_TYPE.hashtag
      ),

  // todo: hashtag rename!
  getRegularAnnotations:
    () =>
    ({ getState }) =>
      getState().annotations.filter(
        (annotation) =>
          annotation.type !== GENERATION_GUIDANCE_TYPE.campaignKeyword &&
          annotation.type !== GENERATION_GUIDANCE_TYPE.hashtag
      ),

  initGenerationLanguage:
    () =>
    ({ getState, setState }, { customer, project }) => {
      const { languageSource } = getState();

      // If generation language was already changed by user - don't rewrite it
      if (languageSource === LANGUAGE_SOURCE.user) {
        return;
      }

      // Customer might not be allowed to change the generation language
      if (!customerCanChangeGenerationLanguage(customer)) {
        setState({
          language: LANGUAGE.DEFAULT,
          languageSource: LANGUAGE_SOURCE.other,
        });
        return;
      }

      // Language from the last generation request
      const latestResult = getProjectLatestGenerationResult(project);
      const lastUsedLanguage = alignLanguage(
        latestResult?.language,
        latestResult?.languageTargetFormality
      );

      // Last user selected language
      const lastSelectedLanguage = alignLanguage(ls.documentGenerationLanguage.read());

      const isLastUsedLanguageInList = LANGUAGE[lastUsedLanguage]
        ? lastUsedLanguage
        : LANGUAGE.DEFAULT;

      const languageOrder = isLastUsedLanguageInList
        ? lastUsedLanguage || lastSelectedLanguage
        : lastSelectedLanguage;

      setState({
        language: languageOrder || LANGUAGE.DEFAULT,
        languageSource: LANGUAGE_SOURCE.other,
      });
    },

  initFromGenerationResult:
    () =>
    ({ setState, dispatch }, { project, isApplicationModeInfinity }) => {
      // We don't care about data derived from "generation results" when working in Infinity context
      if (isApplicationModeInfinity) {
        return;
      }

      const latestResult = getProjectLatestGenerationResult(project);
      const latestUsedTool = actualizeGenerationTool(latestResult?.generationTool);
      if (!isUsedTemplate(latestUsedTool)) {
        return;
      }

      if (isFreestyleGenerationTool(latestUsedTool)) {
        setState({
          generationTool: GENERATION_TOOL.freestyle,
        });
      } else {
        const briefContent = mapBriefFromGenerationResult({
          generationTool: latestUsedTool,
          request: latestResult,
        });

        setState({
          generationTool: latestUsedTool,
          briefContent,
        });

        dispatch(PRIVATE_ACTIONS.storeBriefContentInCache());
      }
    },

  initFreestyleToolFromGenerationResult:
    () =>
    ({ setState }, { project }) => {
      const latestResult = getProjectLatestGenerationResult(project);
      const latestUsedTool = actualizeGenerationTool(latestResult?.generationTool);
      if (latestUsedTool && isFreestyleGenerationTool(latestUsedTool)) {
        setState({
          freestyleToolContent: {
            preloadId: latestResult.freestyleToolId,
            allInstructions: latestResult.freestyleToolAllInstructions,
            allInstructionsData: JSON.parse(latestResult.freestyleToolAllInstructionsData || null),
          },
        });
      }
    },
  validateRewrite:
    () =>
    ({ getState, dispatch }) => {
      const { selectedModeId, generationTool, rewriteText } = getState();

      const isContentImproverTool = generationTool === GENERATION_TOOL.basicContentImprover;

      // general product listing tool should take rewrite text from the brief itself, no need to check it here
      if (
        generationTool === GENERATION_TOOL.generalProductListing &&
        isRecomposeMode(selectedModeId)
      ) {
        return true;
      }

      // Basic tools take rewrite text from the brief itself, no need to check it here
      // Content improver now uses RewriteInput instead of BriefInput so should not be skipped
      const isBasicTool = isBasicGenerationTool(generationTool);
      if (isBasicTool && !isContentImproverTool) {
        return true;
      }

      if ((rewriteText || '').trim().length === 0) {
        dispatch(setError('rewrite', 'Input needed'));
        return false;
      }

      return true;
    },

  validateAnnotationText:
    (value, type) =>
    ({ getState }) => {
      const { generationTool } = getState();
      // Validate new annotation content
      const maxWordsPerKeyword = getToolMaxWordsPerKeyword(generationTool);
      if (maxWordsPerKeyword && countWords(value) > maxWordsPerKeyword) {
        const postfix = pluralize(maxWordsPerKeyword, 'word', 'words');
        let itemTypeLabel;
        switch (type) {
          case GENERATION_GUIDANCE_TYPE.campaignKeyword:
            itemTypeLabel = 'Campaign keywords';
            break;
          default:
            itemTypeLabel = 'Talking points';
        }
        return {
          valid: false,
          message: `${itemTypeLabel} can be only ${maxWordsPerKeyword} ${postfix} long for this template`,
        };
      }

      return { valid: true };
    },

  validateNewAnnotation:
    (content, type) =>
    ({ getState, dispatch }) => {
      const { generationTool } = getState();

      // Do not add empty annotations
      if (content.length === 0) {
        return { valid: false };
      }

      // Skip validations for Infinity "prompt" mode
      if (dispatch(ACTIONS.inExtensionPromptMode())) {
        return { valid: true };
      }

      // Check if user still can "select" annotations
      const isCampaignKeyword = type === GENERATION_GUIDANCE_TYPE.campaignKeyword;
      const isHashtag = type === GENERATION_GUIDANCE_TYPE.hashtag;
      const campaignAnnotations = dispatch(PRIVATE_ACTIONS.getCampaignKeywordsAnnotations());
      const hashtagAnnotations = dispatch(PRIVATE_ACTIONS.getHashtagAnnotations());
      const regularAnnotations = dispatch(PRIVATE_ACTIONS.getRegularAnnotations());
      const maxKeywordsCount = isCampaignKeyword
        ? getToolMaxCampaignKeywords(generationTool)
        : isHashtag
        ? getToolMaxHashtags(generationTool)
        : getToolMaxSuggestKeywords(generationTool) || 1;

      const campaignKeywordsExceedingLimit =
        isCampaignKeyword &&
        campaignAnnotations.length >= getToolMaxCampaignKeywords(generationTool);

      const hashtagsExceedingLimit =
        isHashtag && hashtagAnnotations.length >= getToolMaxHashtags(generationTool);

      const regularKeywordsExceedingLimit =
        !isCampaignKeyword &&
        !isHashtag &&
        regularAnnotations.length >= getToolMaxSuggestKeywords(generationTool);

      if (
        campaignKeywordsExceedingLimit ||
        regularKeywordsExceedingLimit ||
        hashtagsExceedingLimit
      ) {
        const postfix = pluralize(maxKeywordsCount, 'input is', 'inputs are');
        return {
          valid: false,
          message: `Only ${maxKeywordsCount} ${postfix} supported`,
        };
      }

      // Validate new annotation content
      const wordsValidation = dispatch(PRIVATE_ACTIONS.validateAnnotationText(content, type));
      if (!wordsValidation.valid) {
        return wordsValidation;
      }

      return { valid: true };
    },

  storeBriefContentInCache:
    () =>
    ({ getState, setState }) => {
      const { briefContent, briefContentCache, generationTool } = getState();
      const cacheKey = generateBriefCacheKey(generationTool);
      setState({
        briefContentCache: {
          ...briefContentCache,
          [cacheKey]: briefContent,
        },
      });
    },

  storeCustomerPersonaInCache:
    () =>
    ({ getState, setState }) => {
      const { selectedCustomerPersona } = getState();
      setState({
        selectedCustomerPersona: null,
        selectedCustomerPersonaCache: selectedCustomerPersona,
      });
    },

  restoreCustomerPersonaFromCache:
    () =>
    ({ getState, setState }) => {
      const { selectedCustomerPersonaCache } = getState();
      if (selectedCustomerPersonaCache) {
        setState({
          selectedCustomerPersonaCache: null,
          selectedCustomerPersona: selectedCustomerPersonaCache,
        });
      }
    },

  storeRewriteProductNameInCache:
    () =>
    ({ getState, setState }) => {
      const { rewriteProductName } = getState();
      setState({
        rewriteProductName: '',
        rewriteProductNameCache: rewriteProductName,
      });
    },

  restoreRewriteProductNameFromCache:
    () =>
    ({ getState, setState }) => {
      const { rewriteProductNameCache } = getState();
      if (rewriteProductNameCache) {
        setState({
          rewriteProductNameCache: '',
          rewriteProductName: rewriteProductNameCache,
        });
      }
    },

  alignNumberOfVariations:
    () =>
    ({ getState, setState }, { customer }) => {
      const { generationTool } = getState();

      const numberOfVariations = getDocumentDefaultNumberOfVariations(
        generationTool,
        !customerCanChangeNumberOfVariations(customer)
      );
      setState({ numberOfVariations });
    },

  recoverFromCreationError:
    (error) =>
    ({ setState }) => {
      setState({
        errorBriefGeneral: parseErrors(error).join('\n'),
        errorBriefGeneralCode: error.code || null,
      });
    },

  clearBriefUrlErrors:
    () =>
    ({ setState }) => {
      setState({
        errorBriefUrl: null,
        errorBriefGeneral: null,
        errorBriefGeneralCode: null,
      });
    },

  initBriefExtraction:
    () =>
    ({ getState, setState }) => {
      const { briefContent, extractedBriefs } = getState();

      const currentUrls = extractUrls(briefContent?.body, MAX_RESOURCE_PER_TYPE)?.urls ?? [];
      const resolvedUrls = Array.from(Object.keys(extractedBriefs));
      const allResolved = currentUrls.every((url) => resolvedUrls.includes(url));
      if (currentUrls.length === 0) {
        setState({ isExtractingBrief: false });
      } else if (!allResolved) {
        setState({ isExtractingBrief: true });
      }

      setState({ currentUrls });
    },

  updateExtractedBriefErrorsForUrl:
    (url, error) =>
    ({ setState, getState }) => {
      const { extractedBriefErrors } = getState();
      extractedBriefErrors[url] = {
        errorBriefGeneral: parseErrors(error).join('\n'),
        errorBriefGeneralCode: error.code || null,
      };
      setState({ extractedBriefErrors });
    },
};

/**
 * Publicly available actions
 */
export const ACTIONS = {
  initGenerationTool:
    (generationTool, defaultToneOfVoice) =>
    ({ setState, dispatch }, { isApplicationModeInfinity }) => {
      setState({
        generationTool,
        suggestedAnnotationsOrigin: getDefaultSuggestedAnnotationsOrigin(isApplicationModeInfinity),
        freestyleToolContent: EMPTY_FREESTYLE_CONTENT,
        uploadingImages: [],
        uploadingDocuments: [],
        multiPromptFieldImages: {},
        multiPromptFieldDocuments: {},
      });

      dispatch(ACTIONS.initDataDrivenTemplates(defaultToneOfVoice));
      dispatch(ACTIONS.initSubGenerationToolsProps());
      dispatch(ACTIONS.clearBriefError());
      dispatch(ACTIONS.clearRewriteError());
      dispatch(ACTIONS.clearBriefExtractionErrors());
      dispatch(ACTIONS.clearPromptImageErrors());
      dispatch(ACTIONS.clearPromptDocumentErrors());
      dispatch(ACTIONS.setIsUploadingImage(null));

      if (
        doesToolSupportCampaignKeywords(generationTool) &&
        !isRewriteGenerationTool(generationTool)
      ) {
        dispatch(ACTIONS.clearRegularAnnotations());
      } else {
        dispatch(ACTIONS.clearAnnotations());
      }
      dispatch(ACTIONS.clearAnnotationsError());
      if (doesToolSupportsRewriteProductName(generationTool)) {
        dispatch(PRIVATE_ACTIONS.restoreRewriteProductNameFromCache());
      } else {
        dispatch(PRIVATE_ACTIONS.storeRewriteProductNameInCache());
      }
      dispatch(ACTIONS.clearManualInstructions());
      dispatch(ACTIONS.initMaxChars());
      dispatch(ACTIONS.initCasing());
      dispatch(PRIVATE_ACTIONS.alignNumberOfVariations());
      dispatch(ACTIONS.initMultiPromptFields());
      dispatch(ACTIONS.setToolPrompts(null));

      // restore from cache if exists
      dispatch(ACTIONS.restoreGenerationToolOptionsCache());
    },

  initDataDrivenTemplates:
    () =>
    ({ getState, setState }) => {
      const { generationTool } = getState();

      const availableTemplates = getToolGenerationModes(generationTool);
      const visibleTemplates = filterVisibleDDTemplates(availableTemplates);

      setState({
        availableDDTemplates: availableTemplates,
        visibleDDTemplates: visibleTemplates,
        selectedModeId: visibleTemplates[0],
        selectedModeType: GENERATION_MODE_TYPE.common,
        selectedRecomposeTovId: visibleTemplates[0],
        selectedRecomposeTovType: GENERATION_MODE_TYPE.common,
        isCustomFormulasVisible: availableTemplates.includes(AI_SUGGESTION_MODE.customFormula),
      });
    },

  getGenerationTool:
    () =>
    ({ getState }) => {
      return getState().generationTool;
    },

  getFilteredSubGenerationToolsProps:
    (generationCustomModelId) =>
    ({ getState }) => {
      const { subGenerationToolsProps } = getState();
      const filteredSubGenerationToolsProps = {};
      Object.entries(subGenerationToolsProps).forEach(([key, value]) => {
        filteredSubGenerationToolsProps[key] = {
          maxChars:
            generationCustomModelId || value.maxChars == null ? null : parseInt(value.maxChars),
          casing: value.casing,
          suggestionMode: getAlignedSuggestionMode(value.selectedModeId, value.selectedModeType),
        };
        if (
          getAlignedSuggestionMode(value.selectedModeId, value.selectedModeType) ===
          AI_SUGGESTION_MODE.customFormula
        ) {
          filteredSubGenerationToolsProps[key].customFormulaId = value.selectedModeId;
        }
      });
      return filteredSubGenerationToolsProps;
    },

  getAssetType:
    () =>
    ({ getState }) => {
      const { generationTool } = getState();

      return getToolDefaultAssetType(generationTool);
    },

  getBriefContent:
    () =>
    ({ getState }) => {
      return getState().briefContent;
    },

  setBriefContent:
    (briefContent) =>
    ({ setState, dispatch }) => {
      setState({ briefContent });

      dispatch(PRIVATE_ACTIONS.initBriefExtraction());
      dispatch(PRIVATE_ACTIONS.storeBriefContentInCache());
    },

  getRewriteText:
    () =>
    ({ getState }) => {
      const { generationTool, briefContent, rewriteText } = getState();

      if (
        isBasicGenerationTool(generationTool) &&
        generationTool !== GENERATION_TOOL.basicContentImprover
      ) {
        return getRecomposeTextFromBrief(generationTool, briefContent);
      }

      return rewriteText;
    },

  updateRewriteText:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ rewriteText: value });

      dispatch(setError('rewrite', null));
    },
  createMultipleAnnotations:
    (annotations) =>
    ({ getState, dispatch }) => {
      const { generationTool } = getState();

      const isCampaignKeyword = doesToolSupportCampaignKeywords(generationTool);

      annotations.forEach((annotation) => {
        // Add new annotation to the list
        const newAnnotation = {
          id: annotation.id,
          type: isCampaignKeyword
            ? GENERATION_GUIDANCE_TYPE.campaignKeyword
            : GENERATION_GUIDANCE_TYPE.keyword,
          content: annotation,
        };
        dispatch(ACTIONS.addAnnotation(newAnnotation));
      });
    },

  getIsExtractingBrief:
    () =>
    ({ getState }) => {
      return getState().isExtractingBrief;
    },

  validateFreestyleContent:
    () =>
    ({ getState, dispatch }) => {
      // We don't need this validation in Infinity "prompt" mode
      if (dispatch(ACTIONS.inExtensionPromptMode())) {
        return true;
      }

      const { freestyleToolContent } = getState();
      const instructions = { allInstructions: freestyleToolContent.allInstructions };

      const errors = validateFreestyleBrief(instructions);
      if (errors.allInstructions) {
        dispatch(setError('freestyleToolContent', errors));
        return false;
      }

      dispatch(setError('freestyleToolContent', null));
      return true;
    },

  validateBrief:
    (options = null) =>
    ({ getState, setState, dispatch }) => {
      const { generationTool, briefContent } = getState();

      const muteErrors = options?.silent;
      const highlightFields = options?.highlight;

      // Brief is optional for rewrite tools and Infinity "prompt" mode
      const isRewriteTool = isRewriteGenerationTool(generationTool);
      if (
        isRewriteTool ||
        generationTool === GENERATION_TOOL.basicContentImprover ||
        dispatch(ACTIONS.inExtensionPromptMode()) ||
        isMultiPromptTool(generationTool)
      ) {
        return true;
      }

      const briefType = getToolBriefType(generationTool);
      const validation = validateDocumentBrief(briefContent, {
        briefType,
        generationTool,
        skipEmailPromotionField: options?.silent,
      });
      if (!validation.valid) {
        if (!muteErrors) {
          setState({ briefError: omit(validation, ['valid']) });
        }
        return false;
      } else if (highlightFields) {
        setState({ briefError: { highlight: true } });
        return false;
      }

      setState({ briefError: null });
      return true;
    },

  validateParams:
    () =>
    ({ getState, dispatch }) => {
      const { generationTool, callToAction } = getState();

      // If "freestyle" tool selected - run separate validation
      if (isFreestyleGenerationTool(generationTool)) {
        return dispatch(ACTIONS.validateFreestyleContent());
      }

      // Validate "brief" and other generation params
      const briefValid = doesToolSupportPromptInstruction(generationTool)
        ? dispatch(ACTIONS.validateFreestyleContent())
        : dispatch(ACTIONS.validateBrief());

      const shouldRewrite = dispatch(ACTIONS.isShouldRewrite());
      const rewriteValid =
        !shouldRewrite || (shouldRewrite && dispatch(PRIVATE_ACTIONS.validateRewrite()));

      const campaignKeywordsValid = dispatch(ACTIONS.validateCampaignKeywords());
      const manualInstructionsValid = dispatch(ACTIONS.validateManualInstructions());
      const maxCharsValid = dispatch(ACTIONS.validateMaxChars());
      const mixtureToolValid = dispatch(ACTIONS.validateMixtureToolParams());
      const suggestionsManualInstructionsValid = dispatch(
        ACTIONS.suggestionsManualInstructionsValid()
      );
      const callToActionTextValid = validateCallToActionText(callToAction);

      return (
        briefValid &&
        rewriteValid &&
        campaignKeywordsValid &&
        manualInstructionsValid &&
        suggestionsManualInstructionsValid &&
        callToActionTextValid.valid &&
        maxCharsValid &&
        mixtureToolValid
      );
    },

  validateMixtureToolParams:
    () =>
    ({ getState, dispatch }) => {
      const { generationTool, subGenerationToolsProps } = getState();

      if (!isMixtureTool(generationTool)) {
        return true;
      }

      let valid = true;

      for (const [key] of Object.entries(subGenerationToolsProps)) {
        const maxCharsValid = dispatch(ACTIONS.validateSubGenerationToolMaxChars(key));
        if (!maxCharsValid) {
          valid = false;
        }
      }

      if (!valid) {
        dispatch(setError('errorAlert', MAX_CHARS_ERRORS.TOO_SMALL_MULTIPLE));
      } else {
        dispatch(setError('errorAlert', null));
      }
      return valid;
    },

  clearBriefError:
    () =>
    ({ setState, dispatch }) => {
      setState({ briefError: {} });
      dispatch(setError('freestyleToolContent'), null);
    },

  clearRewriteError:
    () =>
    ({ dispatch }) => {
      dispatch(setError('rewrite', null));
    },

  clearBriefExtractionErrors:
    () =>
    ({ setState }) => {
      setState({
        extractedBriefErrors: {},
      });
    },

  selectTemplate:
    (id, type) =>
    ({ getState, setState, dispatch }) => {
      const { generationTool } = getState();

      if (doesToolSupportsPersonaForMode({ generationTool, suggestionMode: id })) {
        dispatch(PRIVATE_ACTIONS.restoreCustomerPersonaFromCache());
      } else {
        dispatch(PRIVATE_ACTIONS.storeCustomerPersonaInCache());
      }
      setState({
        selectedModeId: id,
        selectedModeType: type,
      });
    },

  selectSubGenerationToolTemplate:
    (id, type, subGenerationTool) =>
    ({ getState, setState }) => {
      const { subGenerationToolsProps } = getState();

      if (!subGenerationToolsProps[subGenerationTool]) {
        return;
      }
      subGenerationToolsProps[subGenerationTool].selectedModeId = id;
      subGenerationToolsProps[subGenerationTool].selectedModeType = type;
      const newSubGenerationToolsProps = { ...subGenerationToolsProps };
      setState({ subGenerationToolsProps: newSubGenerationToolsProps });
    },

  clearTemplate:
    () =>
    ({ setState }) => {
      setState({
        selectedModeId: null,
        selectedModeType: null,
      });
    },

  getSubGenerationToolProps:
    (subGenerationTool) =>
    ({ getState }) => {
      const { subGenerationToolsProps } = getState();
      return subGenerationToolsProps[subGenerationTool];
    },

  getSelectedModeId:
    () =>
    ({ getState }) => {
      return getState().selectedModeId;
    },
  getSelectedModeType:
    () =>
    ({ getState }) => {
      return getState().selectedModeType;
    },

  getGenerationModeId:
    () =>
    ({ getState, dispatch }) => {
      const { generationTool, selectedModeId, language } = getState();

      if (
        isNonEnglish(language) ||
        (!dispatch(ACTIONS.isShouldRewrite()) &&
          isBasicGenerationTool(generationTool) &&
          !doesToolHasMultipleGenerationModes(generationTool))
      ) {
        return getToolDefaultGenerationMode(generationTool);
      }

      return selectedModeId;
    },
  getGenerationModeType:
    () =>
    ({ getState }) => {
      const { generationTool, selectedModeType, language } = getState();

      if (isBasicGenerationTool(generationTool) || isNonEnglish(language)) {
        return GENERATION_MODE_TYPE.common;
      }
      return selectedModeType;
    },

  isShouldRewrite:
    () =>
    ({ getState }) => {
      const { generationTool, selectedModeId } = getState();

      if (isRecomposeMode(selectedModeId)) {
        return true;
      }
      return isRewriteGenerationTool(generationTool);
    },

  getSelectedPersona:
    () =>
    ({ getState }) => {
      return getState().selectedCustomerPersona;
    },

  getPersonaForGeneration:
    () =>
    ({ getState }) => {
      const { generationTool, selectedModeId, selectedCustomerPersona } = getState();

      if (
        !doesToolSupportsPersonaForMode({
          generationTool,
          suggestionMode: selectedModeId,
        })
      ) {
        return null;
      }

      return selectedCustomerPersona;
    },

  selectProjectPersona:
    (persona) =>
    async ({ setState }, { project, isApplicationModeInfinity }) => {
      // We do not care about "project.persona" selection persistence in Infinity
      if (isApplicationModeInfinity) {
        setState({ selectedCustomerPersona: persona });
        return;
      }

      setState({ isUpdatingProjectPersonas: true });
      try {
        if (persona) {
          const newValue = persona ? [persona.id] : [];
          await updateProjectPersonas(newValue, project.id);
        }

        setState({ selectedCustomerPersona: persona });
      } finally {
        setState({ isUpdatingProjectPersonas: false });
      }
    },

  validateForGeneration:
    () =>
    ({ getState }) => {
      const { selectedModeType, selectedModeId, generationTool } = getState();
      // We might need more validations here but for now we just check if we have selected mode if and type
      return (
        isMixtureTool(generationTool) ||
        isMultiPromptTool(generationTool) ||
        (selectedModeId && selectedModeType)
      ); //TODO: add validation for sub generation tools
    },

  updateNewAnnotationText:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ newAnnotationText: value });

      // Validate new annotation content
      const wordsValidation = dispatch(PRIVATE_ACTIONS.validateAnnotationText(value));
      if (!wordsValidation.valid) {
        dispatch(setError('annotations', wordsValidation.message));
        return;
      }

      dispatch(ACTIONS.clearAnnotationsError());
    },

  clearAnnotationsError:
    () =>
    ({ dispatch }) => {
      dispatch(setError('annotations', null));
      dispatch(setError('campaignKeywords', null));
      dispatch(setError('annotationsCounter', null));
    },

  clearCallToActionError:
    () =>
    ({ dispatch }) => {
      dispatch(setError('callToAction', null));
    },

  updateNewCampaignKeywordsText:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ newCampaignKeywordsText: value });

      const wordsValidation = dispatch(
        PRIVATE_ACTIONS.validateAnnotationText(value, GENERATION_GUIDANCE_TYPE.campaignKeyword)
      );
      if (!wordsValidation.valid) {
        dispatch(setError('campaignKeywords', wordsValidation.message));
        return;
      }

      dispatch(setError('campaignKeywords', null));
    },

  updateNewHashtagText:
    (value) =>
    ({ setState }) => {
      setState({ newHashtagText: value });
      // current validation is only for words count which is unlimited on hashtags
    },

  validateCampaignKeywords:
    (options = null) =>
    ({ dispatch, getState }) => {
      const { generationTool } = getState();
      const muteErrors = options?.silent;

      // Return early if "campaign keywords" are not supported for the current tool, or is SEOMetadata which the keywords are optional
      if (
        !doesToolSupportCampaignKeywords(generationTool) ||
        isLandingSeoMetaDataGenerationTool(generationTool)
      ) {
        return true;
      }

      const campaignKeywords = dispatch(PRIVATE_ACTIONS.getCampaignKeywordsAnnotations());
      if (campaignKeywords.length === 0) {
        if (!muteErrors) {
          dispatch(setError('campaignKeywords', 'Input needed'));
        }
        return false;
      }

      return true;
    },

  validateMaxChars:
    () =>
    ({ dispatch, getState }) => {
      const { generationTool, maxChars } = getState();

      // "max chars" options is not supported in Infinity "prompt" mode, so we skip validation
      if (dispatch(ACTIONS.inExtensionPromptMode()) || isMixtureTool(generationTool)) {
        return true;
      }

      if (!doesToolSupportMaxChars(generationTool)) {
        return true;
      }

      if (!isInteger(Number(maxChars))) {
        dispatch(setError('maxChars', MAX_CHARS_ERRORS.NAN));
        return false;
      }

      if (Number(maxChars) >= MAX_CHARS_MAX_VALUE) {
        dispatch(setError('maxChars', MAX_CHARS_ERRORS.OVERFLOW));
        return false;
      }

      return true;
    },

  validateSubGenerationToolMaxChars:
    (subGenerationTool) =>
    ({ dispatch, getState }) => {
      const { subGenerationToolsProps } = getState();

      // "max chars" options is not supported in Infinity "prompt" mode, so we skip validation
      if (dispatch(ACTIONS.inExtensionPromptMode())) {
        return true;
      }

      const toolProps = subGenerationToolsProps[subGenerationTool];
      if (!toolProps) {
        return true;
      }
      // Return early if "max chars" is not supported for the current tool or current formula
      if (!doesToolSupportMaxChars(subGenerationTool)) {
        return true;
      }

      if (!isInteger(Number(toolProps.maxChars))) {
        dispatch(setSubGenerationToolError(subGenerationTool, 'maxChars', true));
        dispatch(setSubGenerationToolError(subGenerationTool, 'errorAlert', MAX_CHARS_ERRORS.NAN));
        return false;
      }

      if (Number(toolProps.maxChars) >= MAX_CHARS_MAX_VALUE) {
        dispatch(setSubGenerationToolError(subGenerationTool, 'maxChars', true));
        dispatch(
          setSubGenerationToolError(subGenerationTool, 'errorAlert', MAX_CHARS_ERRORS.OVERFLOW)
        );
        return false;
      }

      return true;
    },

  clearErrorAlertMessage:
    () =>
    ({ dispatch }) => {
      dispatch(setError('errorAlert', null));
    },

  validateManualInstructions:
    (options = null) =>
    ({ dispatch, getState }) => {
      const { generationTool } = getState();
      const muteErrors = options?.silent;

      // Return early if "manual instructions" are not supported for the current tool
      if (!doesToolSupportManualInstructions(generationTool)) {
        return true;
      }

      const manualInstructions = dispatch(ACTIONS.getManualInstructions());
      if (manualInstructions.length > MANUAL_INSTRUCTIONS_MAX_CHARS) {
        if (!muteErrors) {
          dispatch(
            setError('manualInstructions', `${MANUAL_INSTRUCTIONS_MAX_CHARS} characters maximum`)
          );
        }
        return false;
      }

      return true;
    },

  suggestionsManualInstructionsValid:
    () =>
    ({ dispatch, getState }) => {
      const { generationTool, selectedInstructionsOption, rewriteInstructions } = getState();
      const isToolSupportsEnhancedImprovements =
        doesToolSupportEnhancedImprovements(generationTool);
      const manualInstructionsSelected = selectedInstructionsOption === MANUAL_SUGGEST_IMPROVEMENT;

      // Return early if "suggest manual instructions" are not supported for the current tool
      if (!isToolSupportsEnhancedImprovements || !manualInstructionsSelected) {
        return true;
      }

      if (!rewriteInstructions || rewriteInstructions.length === 0) {
        dispatch(setError('suggestionsManualInstructions', EMPTY_FIELD_ERROR));
        return false;
      }

      return true;
    },

  createNewAnnotation:
    ({ content, type, score, origin, id }, options = null) =>
    ({ getState, dispatch }, { notifications }) => {
      const trimmedText = content?.trim();
      const toastValidation = options?.displayValidations === 'toast';
      const isCampaignKeyword = type === GENERATION_GUIDANCE_TYPE.campaignKeyword;
      const isHashtag = type === GENERATION_GUIDANCE_TYPE.hashtag;

      // Validate and display errors
      const validation = dispatch(PRIVATE_ACTIONS.validateNewAnnotation(trimmedText, type));
      if (!validation.valid) {
        if (validation.message) {
          if (toastValidation) {
            notifications.displayWarning(validation.message);
          } else {
            dispatch(
              setError(
                isCampaignKeyword ? 'campaignKeywords' : isHashtag ? 'hashtags' : 'annotations',
                validation.message
              )
            );
            dispatch(setError('annotationsCounter', validation.message));
          }
        }

        return false;
      }

      // Add new annotation to the list
      const newAnnotation = {
        id,
        type: type || GENERATION_GUIDANCE_TYPE.keyword,
        content: trimmedText,
        score,
        origin: origin || USER_ANNOTATIONS_ORIGIN,
      };
      dispatch(ACTIONS.addAnnotation(newAnnotation));

      // Clear "annotations" input
      if (isCampaignKeyword) {
        dispatch(ACTIONS.updateNewCampaignKeywordsText(''));
      } else if (isHashtag) {
        dispatch(ACTIONS.updateNewHashtagText(''));
      } else {
        dispatch(ACTIONS.updateNewAnnotationText(''));
      }
      return true;
    },

  completePendingAnnotation:
    (type) =>
    ({ getState, dispatch }) => {
      const { newAnnotationText, newCampaignKeywordsText, newHashtagText } = getState();
      let text;
      switch (type) {
        case GENERATION_GUIDANCE_TYPE.campaignKeyword:
          text = newCampaignKeywordsText;
          break;
        case GENERATION_GUIDANCE_TYPE.hashtag:
          text = newHashtagText;
          break;
        default:
          text = newAnnotationText;
      }

      const trimmedText = text.trim();

      // Do not add empty annotations
      if (trimmedText.length === 0) {
        return true;
      }

      return dispatch(
        ACTIONS.createNewAnnotation({
          content: trimmedText,
          type,
        })
      );
    },

  addAnnotation:
    (annotation) =>
    ({ getState, dispatch }) => {
      const { annotations } = getState();
      const newList = [...annotations, annotation];
      dispatch(ACTIONS.setAnnotations(newList));
    },

  clearAnnotations:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.setAnnotations([]));
    },

  getAnnotationByContent:
    (content) =>
    ({ getState }) => {
      const { annotations } = getState();
      return annotations.find((annotation) => annotation.content === content);
    },

  clearTalkingPoints:
    () =>
    ({ getState, setState }) => {
      const { annotations } = getState();
      const nonTalkingPoints = isNotTalkingPointAnnotation({ annotations });
      setState({ annotations: nonTalkingPoints });
    },

  clearCampaignKeywordsAnnotations:
    () =>
    ({ getState, setState }) => {
      const { annotations } = getState();
      const nonCampaignKeywordsAnnotations = annotations.filter(
        (annotation) => annotation.type !== GENERATION_GUIDANCE_TYPE.campaignKeyword
      );
      setState({ annotations: nonCampaignKeywordsAnnotations });
    },

  clearRegularAnnotations:
    () =>
    ({ getState, setState }) => {
      const { annotations } = getState();
      const specialAnnotations = annotations.filter(
        (annotation) =>
          annotation.type === GENERATION_GUIDANCE_TYPE.campaignKeyword ||
          annotation.type === GENERATION_GUIDANCE_TYPE.hashtag
      );
      setState({ annotations: specialAnnotations });
    },

  setAnnotations:
    (newAnnotations) =>
    ({ setState, dispatch }) => {
      setState({ annotations: [...newAnnotations] });
    },

  getAnnotationsForGeneration:
    () =>
    ({ getState, dispatch }) => {
      const { generationTool, selectedModeId, annotations } = getState();
      const result = [];

      const isInfinityPromptModeAndGoogleRSA =
        dispatch(ACTIONS.inExtensionPromptMode()) && generationTool === GENERATION_TOOL.googleRSA;

      const toolSupportsSuggestKeywords = doesToolSupportSuggestKeywordsForMode({
        generationTool,
        suggestionMode: selectedModeId,
      });
      if (toolSupportsSuggestKeywords || isInfinityPromptModeAndGoogleRSA) {
        const suggestKeywords = annotations.filter(
          (annotation) =>
            annotation.type !== GENERATION_GUIDANCE_TYPE.campaignKeyword &&
            annotation.type !== GENERATION_GUIDANCE_TYPE.hashtag
        );
        result.push(...suggestKeywords);
      }

      const toolSupportsCampaignKeywords = doesToolSupportCampaignKeywords(generationTool);
      if (toolSupportsCampaignKeywords) {
        const campaignKeywords = annotations.filter(
          (annotation) => annotation.type === GENERATION_GUIDANCE_TYPE.campaignKeyword
        );
        result.push(...campaignKeywords);
      }

      const toolSupportsHashtags = doesToolSupportHashtags(generationTool);
      if (toolSupportsHashtags) {
        const hashtags = annotations.filter(
          (annotation) => annotation.type === GENERATION_GUIDANCE_TYPE.hashtag
        );
        result.push(...hashtags);
      }

      return result;
    },

  hasRegularKeywordsAnnotations:
    () =>
    ({ dispatch }) => {
      const annotations = dispatch(PRIVATE_ACTIONS.getRegularAnnotations());
      return annotations.length > 0;
    },
  removeAnnotation:
    (annotationId) =>
    ({ getState, setState, dispatch }) => {
      const { annotations, newAnnotationText } = getState();
      const newList = annotations.filter(({ id }) => id !== annotationId);
      dispatch(ACTIONS.setAnnotations(newList));

      setState({ newAnnotationText: Object.assign('', newAnnotationText) });
      dispatch(ACTIONS.updateNewAnnotationText(newAnnotationText)); //revalidate error
    },

  getRewriteProductName:
    () =>
    ({ getState }) => {
      const { generationTool, rewriteProductName } = getState();
      return doesToolSupportsRewriteProductName(generationTool) ? rewriteProductName : null;
    },
  updateRewriteProductName:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ rewriteProductName: value });

      dispatch(setError('rewriteProductName', null));
    },

  updateManualInstructions:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ manualInstructions: value });

      dispatch(setError('manualInstructions', null));
    },

  clearManualInstructions:
    () =>
    ({ dispatch }) => {
      dispatch(ACTIONS.updateManualInstructions(''));
    },

  updateMaxChars:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ maxChars: value });
      dispatch(setError('maxChars', null));
      dispatch(setError('errorAlert', null));
      dispatch(setError('formula', null)); //Formula error depends on maxChar input
    },

  updateCasing:
    (value) =>
    ({ setState }) => {
      setState({ casing: value });
    },

  updateSubGenerationToolMaxChars:
    (generationTool, value) =>
    ({ setState, dispatch, getState }) => {
      const { subGenerationToolsProps } = getState();
      if (!subGenerationToolsProps[generationTool]) {
        return;
      }
      subGenerationToolsProps[generationTool].maxChars = value;
      const newSubGenerationToolsProps = { ...subGenerationToolsProps };
      setState({ subGenerationToolsProps: newSubGenerationToolsProps });
      dispatch(setSubGenerationToolError(generationTool, 'maxChars', null));
      dispatch(setSubGenerationToolError(generationTool, 'errorAlert', null));
      dispatch(setSubGenerationToolError(generationTool, 'formula', null)); //Formula error depends on maxChar input
    },

  updateSubGenerationToolCasing:
    (generationTool, value) =>
    ({ setState, getState }) => {
      const { subGenerationToolsProps } = getState();
      if (!subGenerationToolsProps[generationTool]) {
        return;
      }
      subGenerationToolsProps[generationTool].casing = value;
      const newSubGenerationToolsProps = { ...subGenerationToolsProps };
      setState({ subGenerationToolsProps: newSubGenerationToolsProps });
    },

  initMaxChars:
    () =>
    ({ dispatch, getState }) => {
      const { generationTool } = getState();
      if (doesToolSupportMaxChars(generationTool)) {
        dispatch(ACTIONS.updateMaxChars(getDefaultCharsByTool(generationTool)));
      }
    },

  initCasing:
    () =>
    ({ dispatch, getState }) => {
      const { generationTool } = getState();
      if (doesToolSupportMaxChars(generationTool)) {
        dispatch(ACTIONS.updateCasing(getDefaultCaseType(generationTool)));
      }
    },

  initSubGenerationToolsProps:
    () =>
    ({ getState, setState }) => {
      const { generationTool } = getState();
      if (!isMixtureTool(generationTool)) {
        return;
      }
      const subGenerationTools = getToolMixtureSubGenerationTools(generationTool);
      const subGenerationToolsProps = {};
      subGenerationTools.forEach((subGenerationTool) => {
        const availableTemplates = getToolGenerationModes(subGenerationTool);
        const visibleTemplates = filterVisibleDDTemplates(availableTemplates);
        const subGenerationToolsModeOverride =
          getToolMixtureSubGenerationToolsModeOverride(generationTool);
        const selectedModeId = subGenerationToolsModeOverride?.[subGenerationTool]
          ? subGenerationToolsModeOverride?.[subGenerationTool]
          : visibleTemplates[0];
        const selectedModeType = GENERATION_MODE_TYPE.common;
        subGenerationToolsProps[subGenerationTool] = {
          isCustomFormulasVisible: availableTemplates.includes(AI_SUGGESTION_MODE.customFormula),
          maxChars:
            doesToolSupportMaxChars(subGenerationTool) && getDefaultCharsByTool(subGenerationTool),
          casing: getDefaultCaseType(subGenerationTool),
          selectedModeId,
          selectedModeType,
          visibleDDTemplates: visibleTemplates,
          errors: {},
        };
      });
      setState({ subGenerationToolsProps });
    },
  getManualInstructions:
    () =>
    ({ getState }) =>
      trim(getState().manualInstructions),

  getCampaignKeywordsAnnotations: PRIVATE_ACTIONS.getCampaignKeywordsAnnotations,

  initFreestyleToolData:
    (toolData) =>
    ({ setState, dispatch }, { resources }) => {
      if (!toolData) {
        return;
      }

      setState({ freestyleToolContent: { ...toolData } });

      // Check if incoming data has any attached resources
      const allInstructionsData = toolData?.allInstructionsData || {};
      const dataResourceIds = Object.values(allInstructionsData.placeholders || {}).map(
        (placeholder) => placeholder.id
      );
      const dataResources = resources.filter((resource) => dataResourceIds.includes(resource.id));

      // Attach them to the "brief"
      const imageResources = dataResources.filter(isImageResource);
      setState({ uploadingImages: imageResources });
      const documentResources = dataResources.filter(isNonImageFileResource);
      setState({ uploadingDocuments: documentResources });

      dispatch(ACTIONS.updateGenerationToolOptionsCache());
    },

  setFreestyleToolContent:
    (newContent, allInstructionsData, id, isSuggested) =>
    ({ getState, setState, dispatch }) => {
      const { freestyleToolContent, preSuggestedPromptContent } = getState();

      if (freestyleToolContent.allInstructions === newContent) {
        return;
      }

      let personalizedPrompt =
        allInstructionsData.personalizedPrompt || freestyleToolContent.personalizedPrompt;
      let improvedPrompt =
        allInstructionsData.improvedPrompt || freestyleToolContent.improvedPrompt;

      // if the content is not suggested and is empty or the same as the pre-suggested content, we should remove personalized or improved marking
      if (
        !isSuggested &&
        (!slateToText(htmlToSlate(newContent)).trim() ||
          preSuggestedPromptContent.trim() === newContent.trim())
      ) {
        personalizedPrompt = false;
        improvedPrompt = false;
      }

      setState({
        freestyleToolContent: {
          ...freestyleToolContent,
          id,
          allInstructions: newContent,
          allInstructionsData,
          personalizedPrompt,
          improvedPrompt,
        },
      });

      dispatch(setError('freestyleToolContent'), null);
    },
  getFreestyleToolContent:
    () =>
    ({ getState }) => {
      return getState().freestyleToolContent;
    },

  resetFreestyleToolContent:
    () =>
    ({ setState }) => {
      setState({
        freestyleToolContent: EMPTY_FREESTYLE_CONTENT,
      });
    },

  openFreestyleToolSavePopup:
    () =>
    ({ setState }) => {
      setState({
        isFreestyleToolSaveViewVisible: true,
      });
    },

  closeFreestylePopupViews:
    () =>
    ({ setState }) => {
      setState({
        isFreestyleToolSaveViewVisible: false,
        isFreestyleToolConfirmChangesViewVisible: false,
      });
    },

  setNumberOfVariations:
    (numberOfVariations) =>
    ({ setState }) => {
      setState({ numberOfVariations });
    },

  getNumberOfVariations:
    () =>
    ({ getState }) => {
      return getState().numberOfVariations;
    },

  getLanguage:
    () =>
    ({ getState }) => {
      return getState().language;
    },

  getMaxChars:
    () =>
    ({ getState }) =>
      getState().maxChars,

  getCasing:
    () =>
    ({ getState }) =>
      getState().casing,

  setLanguage:
    (language) =>
    ({ setState, dispatch }) => {
      setState({
        language,
        languageSource: LANGUAGE_SOURCE.user,
      });
      dispatch(ACTIONS.clearAnnotations());

      // Save selected language to local storage
      try {
        ls.documentGenerationLanguage.write(language);
      } catch (error) {
        captureMessage("Couldn't persist generation language", {
          level: 'warning',
          extra: {
            error: error.toString(),
            source: 'CanvasProject/LocalStore.setLanguage',
          },
        });
      }
    },

  getLanguageForGeneration:
    () =>
    ({ getState }) => {
      const { language } = getState();
      return normalizeLanguage(language);
    },

  getLanguageTargetFormality:
    () =>
    ({ getState }) => {
      const { language } = getState();
      return getLanguageTargetFormality(language);
    },

  getIsOnlyImproveScore:
    () =>
    ({ getState }) => {
      return getState().isOnlyImproveScore;
    },

  setIsOnlyImproveScore:
    (value) =>
    ({ setState }) => {
      setState({ isOnlyImproveScore: value });
    },

  setGenerating:
    (value) =>
    ({ setState }) => {
      setState({ generating: value });
    },

  setDisabledGeneration:
    (value) =>
    ({ setState }) => {
      setState({ disabledGeneration: value });
    },

  setInfinityExtensionMode:
    (mode) =>
    ({ setState }) => {
      setState({ infinityExtensionMode: mode });
    },

  inExtensionPromptMode:
    () =>
    ({ getState }, { isApplicationModeInfinity }) => {
      const { infinityExtensionMode } = getState();
      return (
        isApplicationModeInfinity &&
        infinityExtensionMode === INFINITY_EXTENSION_MODE.promptGeneration
      );
    },

  getRewriteInstructions:
    () =>
    ({ getState }) =>
      getState().rewriteInstructions,

  setRewriteInstructions:
    (rewriteInstructions) =>
    ({ setState, dispatch }) => {
      setState({ rewriteInstructions });
      dispatch(setError('suggestionsManualInstructions', null));
    },

  setSelectedInstructionsOption:
    (selectedInstructionsOption) =>
    ({ setState }) => {
      setState({ selectedInstructionsOption });
    },

  getSelectedInstructionsOption:
    () =>
    ({ getState }) =>
      getState().selectedInstructionsOption,

  hasBriefErrors:
    () =>
    ({ getState }) => {
      const { errorBriefGeneral, errorBriefGeneralCode } = getState();
      return !!errorBriefGeneral || !!errorBriefGeneralCode;
    },

  validateBriefUrl:
    (generationTool) =>
    ({ getState, setState, dispatch }) => {
      const { briefUrl } = getState();

      // Validate only "URL" first, if it's not valid, no use in validating other fields
      const validationUrl = validateUrl(briefUrl, { generationTool });
      if (!validationUrl.valid) {
        setState({ errorBriefUrl: validationUrl.messages.join() });
        return false;
      }

      // Clear errors
      setState({
        errorBriefUrl: null,
      });
      dispatch(PRIVATE_ACTIONS.clearBriefUrlErrors());

      return true;
    },

  extractedBriefId:
    (url) =>
    ({ getState }) => {
      return getState().extractedBriefId[url];
    },

  shouldExtractUrlBrief:
    () =>
    ({ getState, setState, dispatch }) => {
      const {
        generationTool,
        briefContent,
        extractedBriefIds,
        freestyleToolContent,
        multiPromptFields,
        toolPrompts,
        multiPromptActiveStep,
      } = getState();

      if (!doesToolSupportBackgroundUrlBriefExtraction(generationTool)) {
        return;
      }

      const content =
        isFreestyleGenerationTool(generationTool) ||
        doesToolSupportPromptInstruction(generationTool)
          ? freestyleToolContent?.allInstructions
          : isMultiPromptTool(generationTool)
          ? multiPromptActiveStep === MULTI_PROMPT_TEMPLATE_STEPS[0]
            ? serializeMultiPromptFields(multiPromptFields)
            : serializeToolPrompts(toolPrompts)
          : briefContent?.body;
      const supportedCount = isMultiPromptTool(generationTool) ? 30 : MAX_RESOURCE_PER_TYPE;

      const urls = extractUrls(content, supportedCount)?.urls ?? [];
      setState({ currentUrls: urls });
      if (urls.length === 0) {
        // no urls detected
        return;
      }

      const urlsToExtract = [];
      // Send only new URLs to extraction
      urls.forEach((url) => {
        if (!extractedBriefIds[url]) {
          urlsToExtract.push(url);
        }
      });
      if (urlsToExtract.length === 0) {
        setState({ isExtractingBrief: false });
        return;
      }
      if (doesToolSupportPromptInstruction(generationTool)) {
        setState({ isExtractingBrief: true });
      }
      dispatch(ACTIONS.extractBrief(urlsToExtract));
    },

  extractBrief:
    (urlsToExtract) =>
    ({ getState, setState, dispatch }, { customer }) => {
      const { generationTool, selectedModeId, extractedBriefIds } = getState();

      // for url import, brief type should always be unifiedContent
      const briefType = BRIEF_TYPE.unifiedContent;
      urlsToExtract.forEach((url) => {
        extractBrief({
          customerId: customer?.id,
          url,
          briefType,
          generationTool: alignToolForGeneration(generationTool),
          suggestionMode: selectedModeId,
        })
          .then(({ id }) => {
            extractedBriefIds[url] = id;
            setState({ extractedBriefIds }); // And errors in the catch
            dispatch(ACTIONS.shouldWaitForBriefExtraction(urlsToExtract));
          })
          .catch((error) => {
            dispatch(PRIVATE_ACTIONS.updateExtractedBriefErrorsForUrl(url, error));
            dispatch(PRIVATE_ACTIONS.recoverFromCreationError(error)); // for backward compatibility, remove for multiple
            delete extractedBriefIds[url];
          });
      });
    },

  shouldWaitForBriefExtraction:
    (urls) =>
    ({ getState, setState, dispatch }) => {
      const { generationTool, briefContent } = getState();
      return new Promise((resolve) => {
        if (urls.length === 0) {
          resolve(null);
        }

        const { extractedBriefs, extractedBriefErrors } = getState();
        const resolvedUrls = {};

        const resolveIfReady = () => {
          if (areAllUrlsExtracted(urls, resolvedUrls)) {
            const successfulExtractions = getSuccessfulExtractions(resolvedUrls);
            const hasErrors = urls.length > Object.keys(successfulExtractions).length;
            // This assumes that Social only supports single URL. If reqs change - this should as well.
            let isBlocking = false;
            if (hasErrors) {
              isBlocking = isFreestyleGenerationTool(generationTool)
                ? Object.keys(successfulExtractions).length === 0
                : isEnoughContentForExtraction(briefContent?.body);
            }
            const failedBriefs = urls.filter((url) => Object.hasOwn(extractedBriefErrors, url));
            const { currentUrls } = getState();
            // Since we support only 1 url in social, check the first current url
            if (isEqual(sortBy(currentUrls), sortBy(urls))) {
              setState({ isExtractingBrief: false });
            }
            resolve({
              errors: hasErrors,
              blockingErrors: isBlocking,
              extractedBriefs: successfulExtractions,
              failedBriefs,
            });
          }
        };

        urls.forEach((url) => {
          const inProgressBriefId = getState().extractedBriefIds[url];
          if (extractedBriefs[url]) {
            // already fetched, resolve
            resolvedUrls[url] = extractedBriefs[url];
            resolveIfReady();
          } else if (inProgressBriefId) {
            waitForBriefExtraction(inProgressBriefId)
              .then((extractedBrief) => {
                const { contentTaxonomy, ...newBrief } =
                  mapExtractedContentForEditing(extractedBrief);
                const body = newBrief?.article || '';
                extractedBriefs[url] = body;
                setState({ extractedBriefs });
                resolvedUrls[url] = body;
              })
              .catch((error) => {
                resolvedUrls[url] = null;
                dispatch(PRIVATE_ACTIONS.updateExtractedBriefErrorsForUrl(url, error));
              })
              .finally(() => {
                resolveIfReady();
              });
          } else {
            resolveIfReady();
          }
        });
      });
    },

  getUrlForGeneration:
    () =>
    ({ getState }) => {
      return getState().currentUrls;
    },

  toggleShowAdvancedOptions:
    () =>
    ({ getState, setState }) => {
      const newValue = !getState().showAdvancedOptions;
      setState({ showAdvancedOptions: newValue });
      return newValue;
    },

  setShowAdvancedOptions:
    (value) =>
    ({ setState }) => {
      setState({ showAdvancedOptions: value });
    },

  updateGenerationToolOptionsCache:
    () =>
    ({ getState, setState }) => {
      const {
        generationToolOptionsCache: cache,
        generationTool,
        selectedModeId,
        selectedModeType,
        annotations,
        multiPromptFields,
        toolPrompts,
        chainId,
        csvChainId,
        customMultiPromptId,
        freestyleToolContent,
        subGenerationToolsProps,
        uploadingImages,
        uploadingDocuments,
        numberOfVariations,
        maxChars,
        casing,
      } = getState();

      const cacheKey = toolCacheKey(generationTool, customMultiPromptId);
      setState({
        generationToolOptionsCache: {
          ...cache,
          [cacheKey]: {
            selectedModeId,
            selectedModeType,
            annotations,
            multiPromptFields,
            toolPrompts,
            chainId,
            csvChainId,
            customMultiPromptId,
            freestyleToolContent: cloneDeep(freestyleToolContent),
            subGenerationToolsProps,
            uploadingImages,
            uploadingDocuments,
            numberOfVariations,
            maxChars,
            casing,
          },
        },
      });
    },

  restoreGenerationToolOptionsCache:
    () =>
    ({ getState, setState, dispatch }) => {
      const { generationToolOptionsCache: cache, generationTool, customMultiPromptId } = getState();
      const cacheKey = toolCacheKey(generationTool, customMultiPromptId);
      if (!cache[cacheKey]) {
        return;
      }

      if (cache[cacheKey].selectedModeId) {
        setState({
          selectedModeId: cache[cacheKey].selectedModeId,
          selectedModeType: cache[cacheKey].selectedModeType,
        });
      }
      if (cache[cacheKey].freestyleToolContent) {
        setState({ freestyleToolContent: cache[cacheKey].freestyleToolContent });
      }
      if (cache[cacheKey].annotations) {
        const annotationsList = cache[cacheKey].annotations;
        dispatch(ACTIONS.setAnnotations(annotationsList));
      }
      if (cache[cacheKey].multiPromptFields) {
        dispatch(ACTIONS.setMultiPromptFields(cache[cacheKey].multiPromptFields));
      }
      if (cache[cacheKey].toolPrompts) {
        dispatch(ACTIONS.setToolPrompts(cache[cacheKey].toolPrompts));
      }
      if (cache[cacheKey].chainId) {
        dispatch(ACTIONS.setChainId(cache[cacheKey].chainId));
      }
      if (cache[cacheKey].csvChainId) {
        dispatch(ACTIONS.setCsvChainId(cache[cacheKey].csvChainId));
      }
      if (cache[cacheKey].customMultiPromptId) {
        dispatch(ACTIONS.setCustomMultiPromptId(cache[cacheKey].customMultiPromptId));
        if (cache[cacheKey].subGenerationToolsProps) {
          setState({ subGenerationToolsProps: cache[cacheKey].subGenerationToolsProps });
        }
      }
      if (cache[cacheKey].uploadingImages) {
        setState({ uploadingImages: cache[cacheKey].uploadingImages });
      }
      if (cache[cacheKey].uploadingDocuments) {
        setState({ uploadingDocuments: cache[cacheKey].uploadingDocuments });
      }
      if (cache[cacheKey].numberOfVariations) {
        setState({ numberOfVariations: cache[cacheKey].numberOfVariations });
      }
      if (cache[cacheKey].maxChars) {
        setState({ maxChars: cache[cacheKey].maxChars });
      }
      if (cache[cacheKey].casing) {
        setState({ casing: cache[cacheKey].casing });
      }
      if (cache[cacheKey].subGenerationToolsProps) {
        setState({ subGenerationToolsProps: cache[cacheKey].subGenerationToolsProps });
      }
    },

  getBriefType:
    () =>
    ({ getState }) =>
      getToolBriefType(getState().generationTool),

  hasBriefContent:
    () =>
    ({ getState, dispatch }) => {
      const { briefContent } = getState();
      const briefType = dispatch(ACTIONS.getBriefType());
      return checkBriefHasContent(briefType, briefContent);
    },

  resetSuggestedAnnotationsOrigin:
    () =>
    ({ setState }, { isApplicationModeInfinity }) => {
      const defaultValue = getDefaultSuggestedAnnotationsOrigin(isApplicationModeInfinity);
      setState({ suggestedAnnotationsOrigin: defaultValue });
    },

  suggestPrompt:
    (type, options) =>
    async ({ getState, setState, dispatch }, { project, notifications }) => {
      const { freestyleToolContent, generationTool, generationToolOptionsCache } = getState();
      setState({ isSuggestPromptLoading: true });
      let urlExtractions = [];
      try {
        // wait for brief extraction to finish
        let briefExtraction;
        if (options?.briefExtractionPromise) {
          briefExtraction = await options?.briefExtractionPromise;
          if (briefExtraction?.blockingErrors) {
            return { briefExtractionResult: briefExtraction };
          }

          const extractedBriefs = briefExtraction?.extractedBriefs;
          if (extractedBriefs && Object.keys(extractedBriefs).length > 0) {
            // This is legacy support, to avoid risk the url is used somewhere else in the system (tracking events, for e.g.)
            urlExtractions = Object.entries(extractedBriefs).map(([url, extractedBrief]) => ({
              url,
              extractedBrief,
            }));
          }
        }
        const currentContent = freestyleToolContent.allInstructions;
        const { suggestedPrompt, html, ratingCount } = await suggestPrompt({
          projectId: project.id,
          numberOfVariations: 1,
          freestyleToolAllInstructionsData: JSON.stringify(
            freestyleToolContent.allInstructionsData
          ),
          freestyleToolId: freestyleToolContent.id,
          generationTool: getState().generationTool,
          type,
          urlExtractions,
        });
        if (type === SUGGEST_PROMPT_TYPES.personalize && ratingCount !== null) {
          setState({ isSuggestPromptLoading: false });
          return;
        }

        // cached value must be updated as well
        const cache = { ...generationToolOptionsCache };
        if (cache?.[generationTool]?.freestyleToolContent?.allInstructionsData) {
          cache[generationTool].freestyleToolContent.allInstructionsData.html =
            replacePromptTags(html);
          setState({ generationToolOptionsCache: { ...cache } });
        }

        setState({ preSuggestedPromptContent: currentContent });

        dispatch(
          ACTIONS.setFreestyleToolContent(
            suggestedPrompt,
            {
              html,
              placeholders: {},
              serialized: suggestedPrompt,
              personalizedPrompt: type === SUGGEST_PROMPT_TYPES.personalize,
              improvedPrompt: type === SUGGEST_PROMPT_TYPES.improve,
            },
            null,
            true
          )
        );
        return html;
      } catch (error) {
        //in case of error, go back to not using suggested prompt as none was created.
        notifications.displayError(error);
      } finally {
        setState({ isSuggestPromptLoading: false });
      }
    },

  generationToolRatings:
    () =>
    async ({ getState }, { project, notifications }) => {
      try {
        const generationTool = getState().generationTool;
        if (isBareboneProject(project)) {
          return {};
        }
        const data = await generationToolRatings({
          projectId: project.id,
          generationTool,
        });
        return JSON.parse(data);
      } catch (error) {
        notifications.displayError(error);
      }
    },

  getMultiPromptActiveStep:
    () =>
    ({ getState }) => {
      return getState().multiPromptActiveStep;
    },

  incrementMultiPromptActiveStep:
    () =>
    ({ getState, setState }) => {
      const currentStep = getState().multiPromptActiveStep;
      const currentIndex = getStepIndex(currentStep);
      const isLastStep = currentIndex === MULTI_PROMPT_TEMPLATE_STEPS.length - 1;

      if (isLastStep || currentIndex < 0) {
        return;
      }

      const newActiveStep = MULTI_PROMPT_TEMPLATE_STEPS[currentIndex + 1];
      setState({ multiPromptActiveStep: newActiveStep });
    },

  decrementMultiPromptActiveStep:
    () =>
    ({ getState, setState }) => {
      const currentStep = getState().multiPromptActiveStep;
      const currentIndex = getStepIndex(currentStep);
      const isFirstStep = currentIndex === 0;

      if (isFirstStep || currentIndex < 0) {
        return;
      }

      const newActiveStep = MULTI_PROMPT_TEMPLATE_STEPS[currentIndex - 1];
      setState({ multiPromptActiveStep: newActiveStep });
    },

  resetMultiPromptActiveStep:
    () =>
    ({ setState }) => {
      setState({ multiPromptActiveStep: MULTI_PROMPT_TEMPLATE_STEPS[0] });
    },

  getCallToAction:
    () =>
    ({ getState }) => {
      return getState().callToAction;
    },

  updateCallToAction:
    (value) =>
    ({ setState, dispatch }) => {
      setState({ callToAction: value });
      // Validate new annotation content
      const wordsValidation = validateCallToActionText(value);

      if (!wordsValidation.valid) {
        dispatch(setError('callToAction', wordsValidation.message));
        return;
      }

      dispatch(ACTIONS.clearCallToActionError());
    },

  setSelectedContentImproverTool:
    (selectedContentImproverTool) =>
    ({ setState }) => {
      setState({
        selectedContentImproverTool,
        // clear "content improver context"
        briefContent: {},
      });
    },

  // Multi prompt fields

  initMultiPromptFields:
    (multiPromptFieldData = {}) =>
    ({ setState }) => {
      const multiPromptFields = {};
      multiPromptFieldData?.order?.forEach((multiPromptFieldKey) => {
        multiPromptFields[multiPromptFieldKey] = EMPTY_ALL_INSTRUCTIONS_DATA;
      });

      setState({ multiPromptFields });
    },

  setMultiPromptField:
    (multiPromptFieldName, value) =>
    ({ setState, getState }) => {
      const multiPromptFields = getState().multiPromptFields || {};
      setState({ multiPromptFields: { ...multiPromptFields, [multiPromptFieldName]: value } });
    },

  setMultiPromptFieldImages:
    (image, randomId, index) =>
    ({ setState, getState }) => {
      const { multiPromptFieldImages } = getState();
      if (randomId) {
        if (image) {
          // switch the random id with the real id and info
          const updatedPromptImages = cloneDeep(multiPromptFieldImages);
          const innerIndex = updatedPromptImages[index].findIndex((img) => img.id === randomId);
          updatedPromptImages[index][innerIndex] = { ...image };
          setState({ multiPromptFieldImages: updatedPromptImages });
        } else {
          // add an uploading file
          setState({
            multiPromptFieldImages: {
              ...multiPromptFieldImages,
              [index]: [...(multiPromptFieldImages?.[index] ?? []), { id: randomId }],
            },
          });
        }
      } else {
        if (!multiPromptFieldImages[index]?.some((img) => img.id === image.id)) {
          setState({
            multiPromptFieldImages: {
              ...multiPromptFieldImages,
              [index]: [...(multiPromptFieldImages?.[index] ?? []), image],
            },
          });
        }
      }
    },

  containsId: (images, targetId) => () => {
    for (const key in images) {
      if (images[key].some((image) => image.id === targetId)) {
        return true;
      }
    }
    return false;
  },

  removeIdFromMultiPromptFieldImages:
    (id, index) =>
    ({ setState, getState, dispatch }) => {
      const { multiPromptFieldImages } = getState();
      const updatedPromptImages = cloneDeep(multiPromptFieldImages);
      updatedPromptImages[index] = updatedPromptImages[index].filter((img) => img.id !== id);
      setState({ multiPromptFieldImages: updatedPromptImages });
      if (!dispatch(ACTIONS.containsId(updatedPromptImages, id))) {
        dispatch(ACTIONS.removePromptImage(id));
      }
    },

  setMultiPromptFieldDocuments:
    (document, name, randomId, index) =>
    ({ setState, getState }) => {
      const { multiPromptFieldDocuments } = getState();
      if (randomId) {
        if (document) {
          // switch the random id with the real id and info
          const updatedPromptDocuments = cloneDeep(multiPromptFieldDocuments);
          const innerIndex = updatedPromptDocuments[index].findIndex((img) => img.id === randomId);
          updatedPromptDocuments[index][innerIndex] = { ...document };
          setState({ multiPromptFieldDocuments: updatedPromptDocuments });
        } else {
          // add an uploading file
          setState({
            multiPromptFieldDocuments: {
              ...multiPromptFieldDocuments,
              [index]: [...(multiPromptFieldDocuments?.[index] ?? []), { id: randomId, name }],
            },
          });
        }
      } else {
        if (!multiPromptFieldDocuments[index]?.some((doc) => doc.id === document.id)) {
          setState({
            multiPromptFieldDocuments: {
              ...multiPromptFieldDocuments,
              [index]: [...(multiPromptFieldDocuments?.[index] ?? []), document],
            },
          });
        }
      }
    },

  removeIdFromMultiPromptFieldDocuments:
    (id, index) =>
    ({ setState, getState, dispatch }) => {
      const { multiPromptFieldDocuments } = getState();
      const updatedPromptDocuments = cloneDeep(multiPromptFieldDocuments);
      updatedPromptDocuments[index] = updatedPromptDocuments[index].filter((doc) => doc.id !== id);
      setState({ multiPromptFieldDocuments: updatedPromptDocuments });
      if (!dispatch(ACTIONS.containsId(updatedPromptDocuments, id))) {
        dispatch(ACTIONS.removePromptDocument(id));
      }
    },

  getMultiPromptFieldImages:
    () =>
    ({ getState }) => {
      return getState().multiPromptFieldImages;
    },

  getMultiPromptFieldDocuments:
    () =>
    ({ getState }) => {
      return getState().multiPromptFieldDocuments;
    },

  setMultiPromptFields:
    (multiPromptFields) =>
    ({ setState }) => {
      setState({ multiPromptFields });
    },

  getMultiPromptField:
    (multiPromptFieldName) =>
    ({ getState }) => {
      return getState().multiPromptFields?.[multiPromptFieldName];
    },

  getMultiPromptFieldsForGeneration:
    () =>
    ({ getState }) => {
      const fields = getState().multiPromptFields;
      const fieldsForGeneration = {};
      Object.keys(fields).forEach((field) => {
        const serialized = replaceFilePlaceholders(
          fields[field].serialized.trim(),
          fields,
          field,
          getState().multiPromptFieldDocuments,
          getState().multiPromptFieldImages
        );
        fieldsForGeneration[field] = {
          freestyleToolAllInstructionsData: fields[field],
          index: fields[field]['index'],
          text: serialized,
        };
      });
      return fieldsForGeneration;
    },

  setCustomMultiPromptId:
    (id) =>
    ({ setState }) => {
      setState({ customMultiPromptId: id });
    },

  getCustomMultiPromptId:
    () =>
    ({ getState }) => {
      return getState().customMultiPromptId;
    },

  setChainId:
    (chainId) =>
    ({ setState }) => {
      setState({ chainId });
    },

  setCsvChainId:
    (csvChainId) =>
    ({ setState }) => {
      setState({ csvChainId });
    },

  getChainId:
    () =>
    ({ getState }) => {
      return getState().chainId;
    },

  getCsvChainId:
    () =>
    ({ getState }) => {
      return getState().csvChainId;
    },

  setToolPrompts:
    (toolPrompts) =>
    ({ setState }) => {
      setState({ toolPrompts });
    },

  openManagePersonasPopup:
    () =>
    ({ setState }) => {
      setState({ isManagePersonasPopupOpen: true });
    },

  closeManagePersonasPopup:
    () =>
    ({ setState }) => {
      setState({ isManagePersonasPopupOpen: false });
    },
  updateBrandBriefUsed:
    (id) =>
    async ({ setState }, { project }) => {
      const messagingBank = await updateBrandBriefUsed(id, project.workspaceId);
      setState({ messagingBank });
    },

  addPromptImage:
    (id, data, randomId) =>
    ({ setState, getState }) => {
      const { uploadingImages } = getState();
      if (!randomId) {
        if (!uploadingImages.find((image) => image.id === id)) {
          setState({ uploadingImages: [...uploadingImages, { id, ...data }] });
        }
      } else {
        const index = uploadingImages.findIndex((image) => image.id === randomId);
        const updatedUploadedFile = { id, ...data };
        const updatedUploadingImages = [...uploadingImages];
        updatedUploadingImages[index] = updatedUploadedFile;
        setState({ uploadingImages: updatedUploadingImages });
      }
    },

  removePromptImage:
    (id) =>
    ({ dispatch }) => {
      dispatch(ACTIONS.removeUploadingImage(id));
    },

  getPromptImageIds:
    () =>
    ({ getState }) => {
      return getState().uploadingImages.map(({ id }) => id);
    },

  updatePromptImageErrors:
    (promptImageId, error) =>
    ({ setState, getState }) => {
      const { promptImageErrors } = getState();
      promptImageErrors[promptImageId] = {
        errorImage: parseErrors(error).join('\n'),
        errorImageCode: error.code || null,
      };
      setState({ promptImageErrors });
    },

  clearPromptImageErrors:
    () =>
    ({ setState }) => {
      setState({ promptImageErrors: {} });
    },

  getPromptImageErrors:
    () =>
    ({ getState }) => {
      return getState().promptImageErrors;
    },

  setIsUploadingImage:
    (isUploadingImage) =>
    ({ setState }) => {
      setState({ isUploadingImage });
    },

  getIsUploadingImage:
    () =>
    ({ getState }) => {
      return getState().isUploadingImage;
    },
  getPromptDocumentIds:
    () =>
    ({ getState }) => {
      return getState().uploadingDocuments.map(({ id }) => id);
    },

  addPromptDocument:
    (id, data, randomId) =>
    ({ setState, getState }) => {
      const { uploadingDocuments } = getState();
      if (!randomId) {
        if (!uploadingDocuments.find((document) => document.id === id)) {
          setState({ uploadingDocuments: [...uploadingDocuments, { id, ...data }] });
        }
      } else {
        const index = uploadingDocuments.findIndex((document) => document.id === randomId);
        const updatedUploadedFile = { id, ...data };
        const updatedUploadingDocuments = [...uploadingDocuments];
        updatedUploadingDocuments[index] = updatedUploadedFile;
        setState({ uploadingDocuments: updatedUploadingDocuments });
      }
    },
  removePromptDocument:
    (id) =>
    ({ dispatch }) => {
      dispatch(ACTIONS.removeUploadingDocument(id));
    },

  updatePromptDocumentError:
    (id, error) =>
    ({ setState, getState }) => {
      const { promptDocumentErrors } = getState();
      promptDocumentErrors[id] = {
        errorDocument: parseErrors(error).join('\n'),
        errorDocumentCode: error.code || null,
      };
      setState({ promptDocumentErrors });
    },

  clearPromptDocumentErrors:
    () =>
    ({ setState }) => {
      setState({ promptDocumentErrors: {} });
    },

  setIsUploadingDocument:
    (isUploadingDocument) =>
    ({ setState }) => {
      setState({ isUploadingDocument });
    },

  getIsPersonalizedPrompt:
    () =>
    ({ getState }) => {
      return getState().freestyleToolContent?.personalizedPrompt;
    },

  getIsImprovedPrompt:
    () =>
    ({ getState }) => {
      return getState().freestyleToolContent?.improvedPrompt;
    },

  getGenerationToolOptionsCache:
    () =>
    ({ getState }) => {
      return getState().generationToolOptionsCache;
    },
  getCachedGenerationToolOptions:
    (generationTool) =>
    ({ getState }) => {
      return getState().generationToolOptionsCache?.[generationTool];
    },

  isGenerating:
    () =>
    ({ getState }) => {
      return getState().generating;
    },
  addUploadingDocument:
    (randomId, name, contentType) =>
    ({ setState, getState }) => {
      const { uploadingDocuments } = getState();
      setState({
        uploadingDocuments: [...uploadingDocuments, { id: randomId, label: name, contentType }],
      });
    },
  removeUploadingDocument:
    (id) =>
    ({ setState, getState }) => {
      const { uploadingDocuments } = getState();
      const updatedUploadingDocuments = uploadingDocuments.filter((document) => document.id !== id);
      setState({ uploadingDocuments: updatedUploadingDocuments });
    },
  addUploadingImage:
    (randomId) =>
    ({ setState, getState }) => {
      const { uploadingImages } = getState();
      setState({ uploadingImages: [...uploadingImages, { id: randomId }] });
    },
  removeUploadingImage:
    (id) =>
    ({ setState, getState }) => {
      const { uploadingImages } = getState();
      const updatedUploadingImages = uploadingImages.filter((document) => document.id !== id);
      setState({ uploadingImages: updatedUploadingImages });
    },

  setIsCsvLoading:
    (isCsvLoading) =>
    ({ setState }) => {
      setState({ isCsvLoading });
    },

  setUploadedCsv:
    (uploadedCsv) =>
    ({ setState }) => {
      setState({ uploadedCsv });
    },

  uploadCsv:
    (csvFile) =>
    async ({ dispatch }, { customer, notifications }) => {
      dispatch(ACTIONS.setIsCsvLoading(true));
      try {
        const csv = await uploadCsvFile({
          csv: csvFile,
          customerId: customer.id,
        });
        dispatch(ACTIONS.setUploadedCsv(csv));
      } catch (error) {
        let errorMessage = 'There was an error uploading the file';
        if (error?.message === 'CSV can have a maximum of 100 rows.') {
          errorMessage = 'File exceeds the 100-row limit';
        }
        if (error?.message.startsWith('Invalid CSV encoding or illegal characters')) {
          errorMessage = error.message;
        }
        notifications.displayError({
          message: errorMessage,
        });
      } finally {
        dispatch(ACTIONS.setIsCsvLoading(false));
      }
    },

  getCsvId:
    () =>
    ({ getState }) => {
      return getState().uploadedCsv?.id;
    },

  setImageAspectRatio:
    (imageAspectRatio) =>
    ({ setState }) => {
      setState({ imageAspectRatio });
    },

  getImageAspectRatio:
    () =>
    ({ getState }) => {
      return getState().imageAspectRatio;
    },

  setImageModel:
    (imageModel) =>
    ({ setState }) => {
      setState({ imageModel });
    },

  getImageModel:
    () =>
    ({ getState }) => {
      return getState().imageModel;
    },
};

export const LocalStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: 'view-generation-form',
});
